import { useCallback, useEffect, useState } from 'react';

export type PatientDetailLayout = 'split' | 'overlay';

export interface LayoutPreferences {
  patientDetailLayout: PatientDetailLayout;
}

const STORAGE_KEY = 'zubee.layoutPreferences';

const DEFAULT_PREFERENCES: LayoutPreferences = {
  patientDetailLayout: 'split',
};

function readPreferences(): LayoutPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }
    const parsed = JSON.parse(raw) as Partial<LayoutPreferences>;
    return {
      ...DEFAULT_PREFERENCES,
      ...parsed,
      patientDetailLayout:
        parsed.patientDetailLayout === 'overlay' || parsed.patientDetailLayout === 'split'
          ? parsed.patientDetailLayout
          : DEFAULT_PREFERENCES.patientDetailLayout,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function writePreferences(preferences: LayoutPreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
}

export function useLayoutPreferences() {
  const [preferences, setPreferences] = useState<LayoutPreferences>(readPreferences);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setPreferences(readPreferences());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setPatientDetailLayout = useCallback((patientDetailLayout: PatientDetailLayout) => {
    setPreferences((prev) => {
      const next = { ...prev, patientDetailLayout };
      writePreferences(next);
      return next;
    });
  }, []);

  return {
    patientDetailLayout: preferences.patientDetailLayout,
    setPatientDetailLayout,
  };
}

export const PATIENT_DETAIL_LAYOUT_LABELS: Record<PatientDetailLayout, { title: string; description: string }> = {
  split: {
    title: 'Split view',
    description: 'Patient overview opens in a panel beside the list (50% width).',
  },
  overlay: {
    title: 'Overlay',
    description: 'Patient overview opens as a centered dialog over the workspace.',
  },
};
